import { ref, readonly, onUnmounted } from 'vue';

const useScrollLock = () => {
  const isLocked = ref(false);

  /**
   * Applies CSS properties to an element to prevent scrolling
   */
  const applyScrollLock = (element: HTMLElement): void => {
    element.style.overflow = 'hidden';
    element.style.overscrollBehavior = 'none';
    element.style.height = '100vh';
  };

  /**
   * Removes CSS properties from an element if they match the expected values
   */
  const removeScrollLock = (element: HTMLElement): void => {
    const removePropertyIfMatch = (propertyName: string, expectedValue: string) => {
      if (element.style.getPropertyValue(propertyName) === expectedValue) {
        element.style.removeProperty(propertyName);
      }
    };

    removePropertyIfMatch('overflow', 'hidden');
    removePropertyIfMatch('overscroll-behavior', 'none');
    removePropertyIfMatch('height', '100vh');
  };

  const lockScroll = (): void => {
    if (isLocked.value) {
      return; // Already locked, prevent redundant operations
    }

    const bodyElement = document.body;
    const htmlElement = document.documentElement;

    applyScrollLock(bodyElement);
    applyScrollLock(htmlElement);

    isLocked.value = true;
  };

  const unlockScroll = (): void => {
    if (!isLocked.value) {
      return; // Not locked, nothing to do
    }

    const bodyElement = document.body;
    const htmlElement = document.documentElement;

    removeScrollLock(bodyElement);
    removeScrollLock(htmlElement);

    isLocked.value = false;
  };

  onUnmounted(() => {
    if (isLocked.value) {
      unlockScroll();
    }
  });

  return {
    makePageNotScrollable: lockScroll,
    makePageScrollableAgain: unlockScroll,
    isLocked: readonly(isLocked),
  };
};

export default useScrollLock;
